import { LineItemProps, ServiceCategory, ServiceOffering, ServicesProps } from '@jbhi-fi/apis/src/services/types';
import { JbCart, JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { JbCartItemTransformer, getOfferingForCartItem } from './transformers/JbCartItemTransformer';
import { getServiceOfferings } from '../../../utils/parseServiceOfferings';
import { ServiceIds } from '../../../utils/constants/services';
import { getPrimaryServices, IsAttachedService } from '../pdp/utils/product-service';
import { AttachedServiceProps } from '@jbhi-fi/components/src/Cart/AttachedService/AttachedService';
import { BundleOfferingProps } from '@jbhi-fi/components/src/Cart/BundleOffering/BundleOffering';
import { Facets } from '../pdp/installProductService/types';

export type AttachedServiceEntry = {
  cartItem: JbCartItem;
  serviceData: ServiceOffering;
};

export type CartLineServicesParams = {
  cart?: JbCart | null;
  item: JbCartItem;
};

export type CartLineServicesResult = {
  offering?: ReturnType<typeof getOfferingForCartItem>;
  attachedService?: AttachedServiceProps | undefined; //TODO: use AttachedServiceEntry instead to make it consistent with attached install services
  bundleSuggestion?: BundleOfferingProps | undefined;
  attachedServiceItemsForLine: Array<AttachedServiceEntry>;
  attachedInstallServices: Array<AttachedServiceEntry>;
  productFacets?: Facets;
  primaryServices: Map<ServiceCategory, ServicesProps>;
};

export const getCartLineServices = ({ cart, item }: CartLineServicesParams): CartLineServicesResult => {
  //work out services that are available to offer to the customer
  const cartServiceOfferings = getServiceOfferings();
  const offering = getOfferingForCartItem(item, cartServiceOfferings);
  const fullOffering = offering?.fullOffering;
  const primaryServicesAvailableToOffer = getPrimaryServices(fullOffering);

  const attachedServiceItemsForLine =
    cart?.items
      ?.filter(
        cartItem => IsAttachedService(cartItem) && cartItem.properties?._serviceLink === item.properties?._serviceLink
      )
      .map(cartItem => ({
        cartItem,
        serviceData: tryParseJson<ServiceOffering>(cartItem.properties?._serviceData),
      }))
      .filter((entry): entry is AttachedServiceEntry => !!entry.serviceData) ?? [];

  const attachedService = JbCartItemTransformer.toAttachedServiceProps(item);
  const attachedNonInstallService =
    attachedService?.serviceData?.typeId !== ServiceIds.Install ? attachedService : undefined;

  const attachedInstallServices = attachedServiceItemsForLine.filter(entry => entry.serviceData.typeId === 'install');
  const attachedNonInstallServices = attachedServiceItemsForLine.filter(
    entry => entry.serviceData.typeId !== 'install'
  );

  //remove services that have been attached to the cart item and hence are no longer available to offer to the customer
  if (attachedInstallServices?.length > 0 && primaryServicesAvailableToOffer.has('install')) {
    primaryServicesAvailableToOffer.delete('install');
  }
  if (attachedNonInstallServices?.length > 0 && primaryServicesAvailableToOffer.has('nonInstall')) {
    primaryServicesAvailableToOffer.delete('nonInstall');
  }

  const bundleSuggestion = JbCartItemTransformer.toBundleOfferingProps(item);

  const productFacets = item.onlineProduct?.Display?.SpecificationDetails?.reduce<Facets | undefined>((acc, curr) => {
    const next = acc ?? {};
    next[curr.Name] = curr?.Values;
    return next;
  }, undefined);

  return {
    offering,
    attachedService: attachedNonInstallService,
    bundleSuggestion,
    attachedServiceItemsForLine,
    attachedInstallServices,
    productFacets,
    primaryServices: primaryServicesAvailableToOffer,
  };
};
